/**
 * DONATION FORM COMPONENT
 * 
 * Multi-step donation form with support for local and international payments.
 * 
 * @module Donations/Components/DonationForm
 */

import React, { useState } from 'react';
import {
  HandCoins,
  Smartphone,
  Building2,
  CreditCard,
  Wallet,
  Bitcoin,
  User,
  Mail,
  Phone,
  MessageSquare,
  Heart,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Radio,
  Microscope,
  Landmark,
  GraduationCap,
  HeartPulse,
  Siren,
  PlusCircle
} from 'lucide-react';
import {
  CURRENCIES,
  PAYMENT_METHODS,
  MOBILE_PROVIDERS,
  DONATION_CATEGORIES,
  DEFAULT_DONATION
} from '../constants/donation.constants';
import { DonationCategory as DonationCategoryEnum } from '../types/donation.types';

interface DonationFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const DonationForm: React.FC<DonationFormProps> = ({ onSuccess, onCancel }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_DONATION);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // TODO: Submit donation to API
      console.log('Donation submitted:', formData);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Donation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {[1, 2, 3].map((s) => (
        <div key={s} className="step-dot">
          <div className={`step-circle ${step >= s ? 'active' : ''}`}>{s}</div>
          <span className="step-label">
            {s === 1 && 'Personal Info'}
            {s === 2 && 'Donation Details'}
            {s === 3 && 'Payment'}
          </span>
        </div>
      ))}
    </div>
  );

  const renderPersonalInfoStep = () => (
    <div className="form-step">
      <h3>Your Information</h3>
      <p className="step-description">Tell us who you are so we can thank you properly.</p>

      <div className="form-group">
        <label htmlFor="donorName">
          <User size={16} /> Full Name *
        </label>
        <input
          id="donorName"
          type="text"
          value={formData.donorName}
          onChange={(e) => setFormData({ ...formData, donorName: e.target.value })}
          placeholder="e.g., John Doe"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="donorEmail">
          <Mail size={16} /> Email Address *
        </label>
        <input
          id="donorEmail"
          type="email"
          value={formData.donorEmail}
          onChange={(e) => setFormData({ ...formData, donorEmail: e.target.value })}
          placeholder="john@example.com"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="donorPhone">
          <Phone size={16} /> Phone Number
        </label>
        <input
          id="donorPhone"
          type="tel"
          value={formData.donorPhone || ''}
          onChange={(e) => setFormData({ ...formData, donorPhone: e.target.value })}
          placeholder="e.g., 0977 123 456"
        />
      </div>

      <div className="checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={formData.isAnonymous}
            onChange={(e) => setFormData({ ...formData, isAnonymous: e.target.checked })}
          />
          Donate anonymously
        </label>
        <label>
          <input
            type="checkbox"
            checked={formData.isRecurring}
            onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
          />
          Make this a recurring donation
        </label>
      </div>

      <div className="form-actions">
        <button className="btn-primary" onClick={() => setStep(2)}>
          Next Step <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  const renderDonationDetailsStep = () => {
    const categoryIcons: Record<DonationCategoryEnum, React.ReactNode> = {
      [DonationCategoryEnum.RADIO_PROGRAMS]: <Radio size={16} />,
      [DonationCategoryEnum.RESEARCH]: <Microscope size={16} />,
      [DonationCategoryEnum.GOVERNANCE]: <Landmark size={16} />,
      [DonationCategoryEnum.EDUCATION]: <GraduationCap size={16} />,
      [DonationCategoryEnum.HEALTH]: <HeartPulse size={16} />,
      [DonationCategoryEnum.EMERGENCY]: <Siren size={16} />,
      [DonationCategoryEnum.GENERAL]: <PlusCircle size={16} />
    };

    return (
      <div className="form-step">
        <h3>Donation Details</h3>
        <p className="step-description">How much would you like to give and where should it go?</p>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="amount">
              <HandCoins size={16} /> Amount *
            </label>
            <div className="amount-input-wrapper">
              <input
                id="amount"
                type="number"
                value={formData.amount || ''}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                min="1"
                step="0.01"
                required
              />
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value as any })}
              >
                {CURRENCIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="category">Choose a program to support</label>
          <select
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
          >
            {DONATION_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="message">
            <MessageSquare size={16} /> Message (Optional)
          </label>
          <textarea
            id="message"
            value={formData.message || ''}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Leave a message of support..."
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button className="btn-secondary" onClick={() => setStep(1)}>
            <ArrowLeft size={16} /> Back
          </button>
          <button className="btn-primary" onClick={() => setStep(3)}>
            Next Step <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  const renderPaymentStep = () => {
    const methodIcons: Record<string, React.ReactNode> = {
      mobile_money: <Smartphone size={20} />,
      bank_transfer: <Building2 size={20} />,
      credit_card: <CreditCard size={20} />,
      paypal: <Wallet size={20} />,
      stripe: <CreditCard size={20} />,
      crypto: <Bitcoin size={20} />
    };

    return (
      <div className="form-step">
        <h3>Payment Method</h3>
        <p className="step-description">Choose how you'd like to make your donation.</p>

        <div className="payment-methods-grid">
          {PAYMENT_METHODS.map((method) => (
            <button
              key={method.value}
              className={`payment-method-card ${formData.method === method.value ? 'active' : ''}`}
              onClick={() => setFormData({ ...formData, method: method.value as any })}
            >
              <span className="method-icon">{methodIcons[method.value]}</span>
              <span className="method-label">{method.label}</span>
              {formData.method === method.value && <CheckCircle size={16} className="method-check" />}
            </button>
          ))}
        </div>

        {formData.method === 'mobile_money' && (
          <div className="form-group">
            <label htmlFor="mobileProvider">Select Mobile Money Provider</label>
            <select
              id="mobileProvider"
              value={formData.mobileProvider || ''}
              onChange={(e) => setFormData({ ...formData, mobileProvider: e.target.value as any })}
            >
              {MOBILE_PROVIDERS.map((provider) => (
                <option key={provider.value} value={provider.value}>{provider.label}</option>
              ))}
            </select>
            <input
              type="tel"
              placeholder="Mobile Number (e.g., 0977 123 456)"
              value={formData.mobileNumber || ''}
              onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
            />
          </div>
        )}

        {formData.method === 'bank_transfer' && (
          <div className="bank-transfer-info">
            <h4>Bank Transfer Details</h4>
            <div className="bank-details">
              <p><strong>Bank:</strong> Zambia National Commercial Bank</p>
              <p><strong>Account Name:</strong> Let's Change Zambia</p>
              <p><strong>Account Number:</strong> 1234567890</p>
              <p><strong>Branch:</strong> Lusaka Main</p>
              <p><strong>SWIFT Code:</strong> ZNCOZMLX</p>
            </div>
            <div className="form-group">
              <label htmlFor="reference">Reference</label>
              <input
                id="reference"
                type="text"
                placeholder="e.g., Donation - Your Name"
                value={formData.reference || ''}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              />
            </div>
          </div>
        )}

        {['credit_card', 'paypal', 'stripe', 'crypto'].includes(formData.method) && (
          <div className="international-payment-info">
            <p>International payment options are available via our secure payment partners.</p>
            <p>You will be redirected to complete your payment securely.</p>
          </div>
        )}

        <div className="form-actions">
          <button className="btn-secondary" onClick={() => setStep(2)}>
            <ArrowLeft size={16} /> Back
          </button>
          <button
            className="btn-primary btn-donate"
            onClick={handleSubmit}
            disabled={loading}
          >
            <Heart size={16} />
            {loading ? 'Processing...' : 'Donate Now'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="donation-form-container">
      <h2>
        <HandCoins size={24} className="heading-icon" />
        Make a Donation
      </h2>
      <p className="subtitle">Your support helps us create lasting change in Zambia.</p>

      {renderStepIndicator()}

      {step === 1 && renderPersonalInfoStep()}
      {step === 2 && renderDonationDetailsStep()}
      {step === 3 && renderPaymentStep()}
    </div>
  );
};

export default DonationForm;
